import { Controller } from "stimulus";

// Shared preview side-panel, reused by the file manager (Enviados) and by issued
// documents (Emitidos). It listens for a `file-preview:open` event (dispatched
// on document) whose detail carries the item dataset, and renders one of two
// variants — "file" or "document" — into the same panel chrome.
export default class extends Controller {
  static targets = ["panel", "overlay", "body", "heading"];

  connect() {
    this.onOpenHandler = (event) => this.open(event.detail);
    this.onKeydownHandler = (event) => { if (event.key === "Escape") this.close(); };
    document.addEventListener("file-preview:open", this.onOpenHandler);
    document.addEventListener("keydown", this.onKeydownHandler);
  }

  disconnect() {
    document.removeEventListener("file-preview:open", this.onOpenHandler);
    document.removeEventListener("keydown", this.onKeydownHandler);
  }

  open(detail) {
    const variant = detail.previewVariant === "document" ? "document" : "file";
    this.headingTarget.textContent = variant === "document" ? "Documento emitido" : "Pré-visualização";
    this.bodyTarget.innerHTML = variant === "document" ? this.documentMarkup(detail) : this.fileMarkup(detail);
    this.panelTarget.dataset.open = "true";
    this.overlayTarget.dataset.open = "true";
  }

  close() {
    delete this.panelTarget.dataset.open;
    delete this.overlayTarget.dataset.open;
  }

  // ---------- variants ----------
  fileMarkup(detail) {
    return `
      ${this.hero(detail)}
      <h3 class="text-[16px] font-semibold leading-[1.3] mb-[14px]">${detail.fileName}</h3>
      <div class="grid grid-cols-2 gap-[14px_18px] py-4 border-y border-[rgba(26,24,22,0.08)] mb-[18px]">
        ${this.metaCell("Tipo", detail.fileType)}
        ${this.metaCell("Tamanho", detail.fileSize, true)}
        ${this.metaCell("Local", detail.fileLocation)}
        ${this.metaCell("Modificado", detail.fileDate, true)}
      </div>
      ${this.actions(detail.fileUrl, "Abrir / Baixar")}`;
  }

  documentMarkup(detail) {
    return `
      ${this.hero(detail)}
      <h3 class="text-[16px] font-semibold leading-[1.3] mb-[14px]">${detail.fileName}</h3>
      <div class="grid grid-cols-2 gap-[14px_18px] py-4 border-y border-[rgba(26,24,22,0.08)] mb-[18px]">
        ${this.metaCell("Modelo", detail.docTemplate)}
        ${this.metaCell("Protocolo", detail.docProtocol, true)}
        ${this.metaCell("Emitido em", detail.fileDate, true)}
        ${this.metaCell("Emitido por", detail.docBy)}
        ${this.metaCell("Situação", detail.docStatus)}
        ${this.metaCell("Origem", detail.docSource)}
      </div>
      ${this.actions(detail.fileUrl, "Baixar PDF")}`;
  }

  hero(detail) {
    const isImage = detail.fileKind === "img";
    if (isImage && detail.fileImage) {
      return `<div class="rounded-[12px] h-[240px] bg-cover bg-center border border-[rgba(26,24,22,0.08)] mb-[18px]" style="background-image:url('${detail.fileImage}')"></div>`;
    }
    const color = detail.fileColor || "#7A5C3E";
    return `<div class="rounded-[12px] h-[240px] grid place-items-center border border-[rgba(26,24,22,0.08)] mb-[18px]" style="background:${this.hexWithAlpha(color, 0.1)}"><div style="color:${color}">${this.bigFileIcon()}</div></div>`;
  }

  actions(url, primaryLabel) {
    return `
      <div class="flex gap-2">
        <a href="${url}" target="_blank" class="flex-1 justify-center inline-flex items-center gap-[6px] px-3 py-[9px] rounded-[7px] text-[13px] font-medium text-white bg-[#c85a3e]">${primaryLabel}</a>
        <button type="button" data-action="click->file-preview#close" class="flex-1 justify-center inline-flex items-center px-3 py-[9px] rounded-[7px] text-[13px] font-medium border border-[rgba(26,24,22,0.14)]">Fechar</button>
      </div>`;
  }

  metaCell(label, value, mono = false) {
    const valueClass = mono ? "fm-mono text-[#4a4540]" : "text-[#1a1816]";
    return `<div><div class="text-[10.5px] uppercase tracking-wider text-[#8a847d] mb-1">${label}</div><div class="text-[13px] ${valueClass}">${value || "—"}</div></div>`;
  }

  bigFileIcon() {
    return `<svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5"/></svg>`;
  }

  hexWithAlpha(hex, alpha) {
    const rgbInt = parseInt((hex || "#7A5C3E").slice(1), 16);
    return `rgba(${(rgbInt >> 16) & 255},${(rgbInt >> 8) & 255},${rgbInt & 255},${alpha})`;
  }
}
